import { Fragment, ReactElement, useContext, useEffect, useState } from "react";
import {
  IColumn,
  IEditionResponseWithContent,
  IFilterData,
  IParentSize,
  IState
} from "../../../../types";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import FilterComponentBase from "../../../../components/filterComponentBase";
import { useSelector } from "react-redux";
import { useAsyncFetch, useFetch } from "../../../../system/useFetch";
import SkeletonView from "../../../../components/skeletonView";
import StyledTableRow from "../../../../components/styledTableRow";
import { IShiftReportEdited, IShiftReportMS } from "./types";
import { cloneObject, exportFile, formatDate } from "../../../../system/utils";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ShiftReportEditor from "./shiftReportEditor";
import PublishIcon from "@mui/icons-material/Publish";
import { exportToPDF } from "../../../../system/pdfController";
import { generateDefinition } from "./utils";
import "./styles.css";
import { SystemContext } from "../../../../context/systemContext";
import LockOpenIcon from "@mui/icons-material/LockOpen";

interface IProps {
  parentSize: IParentSize;
}

const columns: IColumn[] = [
  {
    id: "factoryDate",
    label: "Date",
    minWidth: 170
  },
  {
    id: "shift",
    label: "Shift",
    minWidth: 100
  },
  {
    id: "supervisor",
    label: "Supervisor",
    minWidth: 200
  },
  {
    id: "field1",
    label: "Heats during shift",
    minWidth: 200,
  },
  {
    id: "field2",
    label: "Power On",
    minWidth: 200,
  },
  {
    id: "field3",
    label: "Power Off",
    minWidth: 200,
  },
  {
    id: "field4",
    label: "Total Production",
    minWidth: 200,
  },
];

const ShiftReport = ({ parentSize }: IProps): ReactElement => {
  const systemContext  = useContext(SystemContext);
  const filter = useSelector<IState, IFilterData>(state => state.filter);
  const [ reports, setReports ] = useState<IShiftReportMS[] | null>(null);
  const [ editReport, setEditReport ] = useState<IShiftReportMS | null>(null);
  const [ dispatchAPI ] = useAsyncFetch<IEditionResponseWithContent<IShiftReportMS>>();
  const [ getReportById ] = useAsyncFetch<IShiftReportMS | null>();

  const { loading, data } = useFetch<IShiftReportMS[]>(
    {
      module: "shiftReport",
      query: "getReportsByFilter",
      payload: { ...filter.remote }
    });

  const canUnlock = systemContext.user && systemContext.user.permissions.find(x => x.code === "SHIFT_REPORT_MELTSHOP");

  useEffect(() => {
    setReports(cloneObject((data ?? []).sort((a, b) => {
      const diffDay = a.shift.factoryDay - b.shift.factoryDay;
      if (diffDay === 0) {
        return a.shift.name.localeCompare(b.shift.name);
      }

      return diffDay;
    })));
  }, [data]);

  const onEditReport = (item: IShiftReportMS) => {
    if (!item.isSubmitted) {
      getReportById({
        module: "shiftReport",
        query: "getReportById",
        payload: { shiftReportID: item.shiftReportID }
      })
        .then(shiftReport => {
          // console.log("onEditReport.response", response?.content?.safetyResult);
          if (shiftReport) {
            setReports((reports ?? []).map(row => {
              if (row.shiftReportID === shiftReport.shiftReportID) {
                return shiftReport;
              }

              return row;
            }));
            setEditReport(shiftReport);
          }
        });
    }
  };

  const onDownloadPDF = (item: IShiftReportMS) => {
    const fn = async (shiftReport: IShiftReportMS) => {
      const def = generateDefinition(shiftReport);
      const base64 = await exportToPDF(def);

      exportFile(base64, `shift report-${shiftReport.area.code.toLowerCase()}-${shiftReport.shift.name.toLowerCase()}-${formatDate(shiftReport.shift.factoryDay)}.pdf`, );
    };

    getReportById({
      module: "shiftReport",
      query: "getReportById",
      payload: { shiftReportID: item.shiftReportID }
    })
      .then(shiftReport => {
        if (shiftReport) {
          setReports((reports ?? []).map(row => {
            if (row.shiftReportID === shiftReport.shiftReportID) {
              return shiftReport;
            }

            return row;
          }));

          fn(shiftReport).then();
        }
      });
  };

  const onSubmit = (item: IShiftReportMS, force = false) => {
    systemContext.showMessage({
      title: "Submit Report",
      color: "warning",
      message: "Do you want to submit the Report?\nAfter submitting the report it cannot be edited. ",
      callback: () => {
        getReportById({
          module: "shiftReport",
          query: "getReportById",
          payload: { shiftReportID: item.shiftReportID }
        })
        .then(itemUpdated => {
          console.log("onEditReport.response", itemUpdated);
          if (itemUpdated) {

            if (!itemUpdated.isSubmitted || force) {
              const def = generateDefinition(itemUpdated);

              exportToPDF(def).then(base64 => {
                dispatchAPI({
                  module: "shiftReport",
                  query: "submittedReport",
                  payload: {
                    reportID: itemUpdated.shiftReportID,
                    base64,
                    notifyToTopic: "SHIFT_REPORT_MS"
                  }
                }).then(response => {
                  setEditReport(null);
                  if (response?.result) {
                    setReports((reports ?? []).map(row => {
                      if (row.shiftReportID === itemUpdated.shiftReportID) {
                        itemUpdated.isSubmitted = true;

                        return itemUpdated;
                      }

                      return row;
                    }));
                  }
                });
              });
            } else {
              setEditReport(null);
              setReports((reports ?? []).map(row => {
                if (row.shiftReportID === itemUpdated.shiftReportID) {

                  return itemUpdated;
                }

                return row;
              }));
            }
          }
        });
      }
    });
  };

  const onUnlock = (item: IShiftReportMS) => {
    systemContext.showMessage({
      title: "Submit Report",
      color: "warning",
      message: "Do you want to unlock the Report?",
      callback: () => {
        getReportById({
          module: "shiftReport",
          query: "getReportById",
          payload: { shiftReportID: item.shiftReportID }
        })
        .then(shiftReport => {
          // console.log("onEditReport.response", response?.content?.safetyResult);
          if (shiftReport && shiftReport.isSubmitted) {
            dispatchAPI({
              module: "shiftReport",
              query: "unlockReport",
              payload: {
                reportID: shiftReport.shiftReportID
              }
            }).then(response => {
              setEditReport(null);
              if (response?.result) {
                setReports((reports ?? []).map(row => {
                  if (row.shiftReportID === shiftReport.shiftReportID) {
                    row.isSubmitted = false;
                  }

                  return row;
                }));
              }
            });
          } else if (shiftReport) {
            setEditReport(null);
            setReports((reports ?? []).map(row => {
              if (row.shiftReportID === shiftReport.shiftReportID) {
                return shiftReport;
              }

              return row;
            }));
          }
        });
      }
    });
  };

  const onAccept = (report: IShiftReportEdited) => {
    dispatchAPI({
      module: "shiftReport",
      query: "insertOrUpdateReport",
      payload: { report, notify: false }
    }).then(response => {
      setEditReport(null);
      if (response?.result) {
        setReports((reports ?? []).map(row => {
          if (row.shiftReportID === report.shiftReportID) {
            return response.content ?? row;
          }

          return row;
        }));
      }
    });
  };

  return (
    <div className="display-flex-column" style={{ height: "100%" }}>
      <FilterComponentBase loading={false} disabled={{ crew: true, shift: true }}/>
      { loading && <SkeletonView  parentSize={parentSize}/> }
      { !loading &&
        <TableContainer className="flex-grow-1">
          <Table stickyHeader={true}  size={"small"}>
            <TableHead>
              <TableRow>
                {columns.map((column) => {
                  return (
                    <TableCell
                      key={`${column.id}`}
                      style={{ backgroundColor: "#b0bec5", minWidth: column.minWidth }}>
                      {column.id !== "actions" ? column.label : <Fragment /> }
                    </TableCell>
                  );
                })}
                <TableCell style={{ backgroundColor: "#b0bec5", minWidth: 60 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {reports && reports.map(row => {
                const isOwner = row.user && systemContext.user && systemContext.user.userID === row.user.userID;

                return (
                  <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`shift-row-${row.shiftReportID}`} onDoubleClick={() => onEditReport(row)}>
                    <TableCell>
                      {formatDate(row.shift.factoryDay)}
                    </TableCell>
                    <TableCell>
                      {row.shift.name}
                    </TableCell>
                    <TableCell>
                      {row.user && row.user.userName}
                    </TableCell>
                    <TableCell>
                      {row.content && row.content.production.heatsDuringShift.value}
                    </TableCell>
                    <TableCell>
                      {row.content && row.content.production.powerOn.value}
                    </TableCell>
                    <TableCell>
                      {row.content && row.content.production.powerOff.value}
                    </TableCell>
                    <TableCell>
                      {row.content && row.content.production.totalProduction.value}
                    </TableCell>
                    <TableCell>
                      <div className="display-flex-row gap-1">
                        <Tooltip title="Edit">
                          <span>
                            <IconButton size={"small"} disabled={row.isSubmitted} onClick={() => onEditReport(row)} color="info">
                              <EditIcon/>
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Submit">
                          <span>
                            {/*<IconButton size={"small"} disabled={!row.content} onClick={() => onSubmit(row, true)} color="success">*/}
                            <IconButton size={"small"} disabled={row.isSubmitted || !row.content} onClick={() => onSubmit(row)} color="success">
                              <PublishIcon/>
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="PDF">
                          <span>
                            {/*<IconButton size={"small"}  onClick={() => onDownloadPDF(row)} color="secondary">*/}
                            <IconButton size={"small"} disabled={!row.isSubmitted} onClick={() => onDownloadPDF(row)} color="secondary">
                              <PictureAsPdfIcon/>
                            </IconButton>
                          </span>
                        </Tooltip>
                        { (canUnlock || isOwner) &&
                          <Tooltip title="Unlock">
                            <span>
                              <IconButton size={"small"} disabled={!row.isSubmitted} onClick={() => onUnlock(row)} color="warning">
                                <LockOpenIcon/>
                              </IconButton>
                            </span>
                          </Tooltip>
                        }
                      </div>
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      }
      {editReport && <ShiftReportEditor reportData={editReport} onAccept={onAccept} onClose={() => setEditReport(null)} />}
    </div>
  );
};


export default ShiftReport;